/* The ONE shared intake story (designs/intake-about.md AB6/AB7) — why the
   org runs an intake, what happens to an effort after it is filed, and what
   the tool does at each step. Held ABOVE the seven domains, as data.

   This exists because AB5 got it half right: the tool story really is the
   same for every domain "by construction" — and was therefore being printed
   seven times inside seven domain pages. Settled decision #1 of this project
   is one canonical TEMPLATE with per-project VALUES; the intake process is
   the template and a domain's taxonomy is its values, so seven copies of
   the process was the stale-Word-document pattern Hermes exists to replace,
   on Hermes's own About page.

   HONESTY: unlike the per-domain entries (intake-about.ts), this record is
   `confirmed`. It describes behaviour we wrote and a process this repo
   records — not research drafted without the team in the room. That
   distinction is the point of the split: the "drafted from research"
   banner used to render above BOTH tabs, so on six of seven domains it sat
   over a tool description that was never drafted from anything.

   Sep 14 2026 (intake-unified-form.md UF11): the per-domain tabs and their
   About pages are cut, so this is the ONLY About, and the AB7 exceptions
   list went with the domain registry it was checked against. */

export interface LifecycleStage {
  /* stable key, so the page can order and test them without matching prose */
  key: "submit" | "dedup" | "groom" | "disposition";
  title: string;
  body: string;
}

export interface ToolCapability {
  title: string;
  what: string;
  why: string;
}

export interface ModuleAbout {
  intro: string;
  whyHeading: string;
  why: readonly string[];
  lifecycleHeading: string;
  lifecycle: readonly LifecycleStage[];
  capabilitiesHeading: string;
  capabilities: readonly ToolCapability[];
  /* the short form the section ⓘ discloses (help.ts SECTION_ABOUT) — drawn
     from `intro` so the two can never drift apart */
  shortWhat: string;
  shortWhy: string;
  shortHow: string;
  confirmed: true;
}

const INTRO =
  "Intake is the register of large engineering efforts across the robots, in one place, in one " +
  "shape — so two teams never build the same thing. A request to a platform team is one kind of entry, not the " +
  "purpose. The hard part was never collecting requests: it is seeing when several teams need the " +
  "same thing, deciding who builds it, and being able to report honestly on what was promised. " +
  "This page describes the process every effort goes through.";

export const INTAKE_ABOUT_MODULE: ModuleAbout = {
  intro: INTRO,
  whyHeading: "Why the org needs an intake at all",
  why: [
    "Before this, an effort reached a platform team through whichever channel its requester " +
      "happened to use — a meeting, an email, a hallway. Nothing compared two teams' efforts, so " +
      "the same need arrived three times under three names and the loudest team was served first.",
    "Nothing recorded what happened next either. An effort that was accepted and an effort that was " +
      "quietly dropped looked identical from the outside, which is why requesting teams asked " +
      "again rather than waiting.",
    "One intake fixes both: every effort is comparable to every other, and its state is a " +
      "matter of record rather than of memory.",
  ],
  lifecycleHeading: "What happens to an effort",
  lifecycle: [
    {
      key: "submit",
      title: "1 — A team registers it",
      body:
        "Any signed-in team files one form: what the effort is, why, who is the point of " +
        "contact, its status, by when — in plain words. You do not have to know which platform " +
        "team or discipline owns it; the platform managers route it when they groom. Similar " +
        "efforts appear as you type. Nothing is assumed on your behalf: a value you do not have " +
        "is recorded as not recorded, never guessed at.",
    },
    {
      key: "dedup",
      title: "2 — Near-duplicates become one shared effort",
      body:
        "On submit, a similarity check looks across every effort for one like yours. If one " +
        "exists you can add your team to it (+1) instead of filing a second copy — so a need " +
        "wanted by five teams is one request with five backers, which is what makes it rank.",
    },
    {
      key: "groom",
      title: "3 — The platform managers groom, route, and decide the outcome",
      body:
        "A platform manager grooms the effort from the queue — priority, size, RAG health — " +
        "and names the owning team(s), which is what routes it. Until then it sits under 'Nobody " +
        "owns this yet'. Once it is groomed it gets one outcome: it becomes a new project; it is " +
        "covered by an existing project, or expands one's scope; platform takes it on because " +
        "another team needs it too; the team builds it with a named platform reviewer in its " +
        "designs and reviews; or it is a duplicate of another effort and folds into it. Score is " +
        "value × reach ÷ effort, so shared and cheap work floats up on its own.",
    },
    {
      key: "disposition",
      title: "4 — A groomed effort becomes work, or an honest no",
      body:
        "From its outcome the work is tracked on the project record, and the delivery chain " +
        "follows it to verified. An effort that is rejected says so, with who decided, when, and " +
        "why.",
    },
  ],
  capabilitiesHeading: "What the tool does about each",
  capabilities: [
    {
      title: "One shared picture",
      what:
        "Every team's efforts land on one queue, grouped by who owns them. On " +
        "submit a similarity check catches near-duplicates, so a need wanted by several teams " +
        "becomes one shared request rather than five copies.",
      why:
        "Replaces scattered requests and the 'loudest team gets served' dynamic with a single, " +
        "deduplicated source of truth the whole company can see.",
    },
    {
      title: "Prioritize by value and reach, not volume",
      what:
        "Each effort carries a reach-weighted score (value × how many teams it serves ÷ effort) and " +
        "a committed-this-cycle flag. Sort by score to see what to do first.",
      why:
        "Turns 'every team says Must-have' into a rankable number. Shared, cheap, high-value work " +
        "floats up automatically — leverage for a small team, and an intake meeting that makes a " +
        "decision rather than reading out a status.",
    },
    {
      title: "Coordinate on the item, not in email",
      what: "Every effort has a discussion thread, so the conversation lives with the record.",
      why:
        "The ad-hoc 'what requirements are you using?' questions live with the effort, so context is " +
        "not lost and nobody reconstructs a decision from chat history.",
    },
    {
      title: "Track to verified, not just 'accepted'",
      what:
        "A delivery chain per effort — Charter → Requirement → Jama → Code → Test cases → Test " +
        "artifacts → Verified — with progress on the board.",
      why:
        "Directly attacks the 'design runs ahead of requirements' problem: an accepted feature can " +
        "no longer hide that its requirements or tests do not exist yet.",
    },
    {
      title: "Report up automatically",
      what:
        "An exec rollup groups efforts by market, with RAG health, committed and " +
        "demo-critical counts, and average delivery progress. The full list exports to CSV.",
      why:
        "The same tool that runs the meeting produces the Profitable Business Review picture — no " +
        "re-keying a status table the night before.",
    },
    {
      title: "Keep the queue organized",
      what:
        "Admins groom, set owners on both sides, place work on the roadmap and archive — every " +
        "change an appended revision. The team list is one controlled vocabulary, curated on " +
        "the Teams section; products, their markets and vehicles on Products.",
      why:
        "Nothing rots ungroomed, ownership is explicit on both sides, and every change is " +
        "recorded — so the picture stays trustworthy as it scales.",
    },
  ],
  shortWhat:
    "Intake is the register of large engineering efforts across the robots, in one place, in one " +
    "shape — so two teams never build the same thing.",
  shortWhy:
    "It makes every effort comparable to every other, lets several teams back one shared " +
    "request instead of filing five copies, and puts the state of an effort on the record rather " +
    "than in someone's memory.",
  shortHow:
    "One form for every effort and one queue grouped by who is building it. Register in plain " +
    "words with a point of contact, back an existing effort with a +1, and follow it from groomed " +
    "and routed to its outcome. Open About intake for the whole process.",
  confirmed: true,
};

/* BLANK, not empty (PR-113 review). "" was the first cut's test, so a
   whitespace-only heading walked straight past the guard and shipped a page
   with an invisible gap in it. Every prose check below goes through here. */
const blank = (s: string): boolean => s.trim() === "";
const named = (s: string): string => (blank(s) ? "(untitled)" : s);

/* The registry's own invariant check — the idiom aboutReason() and
   wizardTreeReason() already use. Returns a reason the shared story is
   broken, or null.

   It takes the record as a PARAMETER (defaulted), unlike its older
   siblings, so its rejection branches can actually be exercised: a guard
   whose only assertion is "returns null for the real record" has never been
   seen to say no, and may not. The negative cases live in
   intake-about-module.test.ts. */
export const moduleAboutReason = (m: ModuleAbout = INTAKE_ABOUT_MODULE): string | null => {
  const prose: readonly (readonly [string, string])[] = [
    ["intro", m.intro],
    ["whyHeading", m.whyHeading],
    ["lifecycleHeading", m.lifecycleHeading],
    ["capabilitiesHeading", m.capabilitiesHeading],
    /* the short form feeds the section ⓘ (help.ts) — blank there is a
       disclosure that discloses nothing, the exact bug AB6 set out to fix */
    ["shortWhat", m.shortWhat],
    ["shortWhy", m.shortWhy],
    ["shortHow", m.shortHow],
  ];
  const emptyField = prose.find(([, value]) => blank(value));
  if (emptyField) return `the shared intake story's ${emptyField[0]} is blank`;

  if (m.why.length === 0) return "the shared story never says why the org needs an intake";
  if (m.why.some(blank)) return "the shared story has a blank 'why' paragraph";

  const stages: LifecycleStage["key"][] = ["submit", "dedup", "groom", "disposition"];
  const got = m.lifecycle.map((s) => s.key);
  if (got.length !== stages.length || got.some((k, i) => k !== stages[i]))
    return `the lifecycle must read ${stages.join(" → ")} (got ${got.join(" → ") || "nothing"})`;
  const thinStage = m.lifecycle.find((s) => blank(s.title) || blank(s.body));
  if (thinStage) return `lifecycle stage "${thinStage.key}" is missing its title or body`;

  const thin = m.capabilities.find((c) => blank(c.title) || blank(c.what) || blank(c.why));
  if (thin) return `capability "${named(thin.title)}" is missing what it does or why it helps`;

  return null;
};
